import { useState, useEffect } from "react";
import StepCard from "./StepCard";
import QuestionForm from "./QuestionForm";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/SocketContext";

const USACompanyFormation = () => {
  const { currentThemeColors } = useTheme();
  const [steps, setSteps] = useState([
    { step: 1, title: "Company formation checklist", status: "in-progress", progress: 0, answers: {}, comments: {}, file: null },
    { step: 2, title: "Post Formation documents", status: "pending", progress: 0 },
    { step: 3, title: "Tax ID (EIN)", status: "pending", progress: 0 },
  ]);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [assignedCompanyForm, setAssignedCompanyForm] = useState("LLC");
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Calculate total questions based on company type
  const getTotalQuestions = () => {
    if (assignedCompanyForm === "C-Corp") {
      // C-Corp: Basic Info (6) + President (3) + Secretary (3) + Treasurer (6) + Responsible Party (12) = 30
      return 30;
    } else {
      // LLC: Basic Info (6) + Partner 1 (3) + Partner 2 (3) + Partner 3 (3) + Responsible Party (11) = 26
      return 26;
    }
  };
  
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        setUserData(data.user);
        
        // Initialize company form type from user data or default to LLC
        const companyForm = data.user.assignedCompanyForm || "LLC";
        setAssignedCompanyForm(companyForm);

        // Initialize steps
        if (data.user.companySteps && data.user.companySteps.length > 0) {
          setSteps(data.user.companySteps);
          
          // If we have step 1 data, make sure the company type matches
          const step1 = data.user.companySteps.find(s => s.step === 1);
          if (step1 && step1.answers && step1.answers.businessType) {
            setAssignedCompanyForm(step1.answers.businessType);
          }
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error(error);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleStepClick = (stepNumber) => {
    if (stepNumber === 1) {
      if (steps[0].status === "submitted") {
        alert("Step 1 has already been submitted and cannot be reopened.");
        return;
      }
      setIsFormVisible(true);
    }
  };

  const handleFormSubmit = async (data, isFinalSubmit) => {
    const answeredCount = Object.keys(data.answers).filter((k) => data.answers[k]).length;
    const token = localStorage.getItem("token");
    
    // Determine the new status based on whether this is a final submit
    const newStatus = isFinalSubmit ? "submitted" : "in-progress";
    
    // First, update the UI optimistically
    const updatedSteps = steps.map((s) => {
      if (s.step === 1) {
        return {
          ...s,
          status: newStatus,
          progress: answeredCount,
          answers: data.answers,
          comments: data.comments,
          file: data.file || s.file,
          error: false,
          errorMessage: ''
        };
      }
      // If this is a final submit, update step 2 to in-progress
      if (s.step === 2 && isFinalSubmit) {
        return {
          ...s,
          status: "in-progress"
        };
      }
      return s;
    });

    // Update the UI immediately
    setSteps(updatedSteps);
    setError(null);

    try {
      // Prepare the data in the format expected by the backend
      const requestData = {
        steps: updatedSteps.map(step => ({
          step: step.step,
          title: step.title,
          status: step.status,
          progress: step.progress || 0,
          answers: step.answers || {},
          comments: step.comments || {},
          file: step.file || null
        })),
        answers: data.answers || {},
        comments: data.comments || {},
        file: data.file || null
      };

      console.log('Sending update request:', requestData);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/update-steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json().catch(() => ({}));
      console.log('Update response:', { status: response.status, responseData });

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }

      // If we get here, the update was successful
      if (isFinalSubmit) {
        alert("Form submitted successfully!");
        setIsFormVisible(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Update steps with error state
      const errorSteps = steps.map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            status: 'in-progress',
            error: true,
            errorMessage: error.message || 'Failed to save progress. Please try again.'
          };
        }
        return s;
      });
      
      setSteps(errorSteps);
      setError(error.message || 'Failed to save progress. Please try again.');
    }
  };

  // Don't render anything until we've finished initializing
  if (loading || !isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading your USA Company Formation information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 mb-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Instruction Banner */}
      <div 
        className="relative overflow-hidden rounded-2xl px-4 py-2 shadow-lg opacity-0 animate-fade-in-up"
        style={{ 
          animationDelay: '0ms', 
          animationFillMode: 'forwards',
          background: `linear-gradient(135deg, var(--primary-600), var(--primary-700))`
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMThjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="relative flex items-center gap-3 text-white">
          <span className="text-2xl">🏢</span>
          <marquee
            className="text-sm font-medium"
            behavior="scroll"
            direction="left"
            scrollamount="5"
          >
            USA Formation: Start with Company Formation Checklist ➡️ Post Formation Documents ➡️ Tax ID (EIN) | 💬 Use Chat with Us to communicate | 📝 Check Tax Filing for instructions
          </marquee>
        </div>
      </div>

      {/* Page Header */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-dark-800 dark:text-white">{userData ? userData.name : ''}</h1>
            {userData?.email && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{userData.email}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 text-sm text-gray-600 dark:text-gray-400">
            <span className="px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold">
              USA Company Formation
            </span>
            <span className="px-3 py-1 rounded-full bg-secondary-50 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-400 font-semibold">
              {assignedCompanyForm}
            </span>
          </div>
        </div>
      </div>

      {/* Steps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {steps.map((s, index) => (
          <div 
            key={`wrapper-${s.step}`}
            className="opacity-0 animate-fade-in-up"
            style={{ animationDelay: `${200 + index * 100}ms`, animationFillMode: 'forwards' }}
          >
          <StepCard
            step={s.step}
            title={s.title}
            status={s.status}
            progress={s.progress}
            totalQuestions={s.step === 1 ? getTotalQuestions() : 1}
            error={s.error || false}
            errorMessage={s.errorMessage || ''}
            onClick={() => handleStepClick(s.step)}
          />
          </div>
        ))}
      </div>

      {isFormVisible && (
        <QuestionForm
          onClose={() => setIsFormVisible(false)}
          onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal)}
          initialAnswers={steps[0].answers}
          initialComments={steps[0].comments}
          isSubmitted={steps[0].status === "submitted"}
          companyType={assignedCompanyForm}
        />
      )}
    </div>
  );
};

export default USACompanyFormation;